/**
 * Authentication Service
 * API calls related to authentication
 *
 * Authentication uses session_id stored in localStorage and sent via Authorization header.
 */

import axiosInstance, { clearAuthData } from '../axiosInstance';
import { API_CONFIG } from '../../config/api.config';
import { LoginResponse, UserInfoResponse } from '../../types';

export interface LoginCredentials {
  email: string;
  password: string;
  applicationType: string;
  isEmail: boolean;
}

/**
 * Session ID management helpers
 */
export const setSessionId = (sessionId: string): void => {
  if (sessionId) {
    localStorage.setItem('session_id', sessionId);
    console.log('✅ Session ID stored:', sessionId.substring(0, 8) + '...');
  } else {
    localStorage.removeItem('session_id');
  }
};

export const getSessionId = (): string | null => {
  return localStorage.getItem('session_id');
};

export const clearSessionId = (): void => {
  localStorage.removeItem('session_id');
};

/**
 * Login user
 * Extracts and stores session_id from login response.
 */
export const login = async (
  email: string,
  password: string,
  applicationType: string = 'Buyer',
  isEmail: boolean = true
): Promise<LoginResponse> => {
  const response = await axiosInstance.post<LoginResponse>(
    API_CONFIG.ENDPOINTS.AUTH.LOGIN,
    {
      email,
      password,
      applicationType,
      isEmail,
    }
  );

  // Store session_id if login successful
  if (response.data.status === true && response.data.session_id) {
    setSessionId(response.data.session_id);
    console.log('🔐 Login successful - session_id stored');
  } else if (response.data.status === true && !response.data.session_id) {
    console.warn('⚠️ Login successful but no session_id in response');
  } else {
    console.error('❌ Login failed:', response.data);
  }

  return response.data;
};

/**
 * Get user info after login
 * Session ID is sent automatically via Authorization header.
 */
export const getUserInfo = async (): Promise<UserInfoResponse> => {
  const sessionId = getSessionId();

  console.log('📞 Calling getUserInfo:', {
    endpoint: API_CONFIG.ENDPOINTS.USERS.USER_INFO,
    hasSessionId: !!sessionId,
    sessionIdPreview: sessionId ? sessionId.substring(0, 8) + '...' : 'none'
  });

  const response = await axiosInstance.get<UserInfoResponse>(
    API_CONFIG.ENDPOINTS.USERS.USER_INFO
  );

  console.log('✅ getUserInfo response:', response.data);
  return response.data;
};

/**
 * Logout user
 * Clears session_id and user data from localStorage.
 */
export const logout = async (): Promise<void> => {
  try {
    console.log('🔓 Calling logout API:', {
      endpoint: API_CONFIG.ENDPOINTS.AUTH.LOGOUT
    });

    await axiosInstance.post(API_CONFIG.ENDPOINTS.AUTH.LOGOUT);

    console.log('✅ Logout API call successful');
  } catch (error) {
    console.error('❌ Logout API error:', error);
    // Continue to clear local data even if API fails
  } finally {
    // Clear session and user data
    clearAuthData();
    console.log('🧹 Local auth data cleared');
  }
};

/**
 * Refresh auth token
 * Session ID is sent via Authorization header.
 */
export const refreshToken = async (): Promise<void> => {
  console.log('🔄 Refreshing session');

  const response = await axiosInstance.post(
    API_CONFIG.ENDPOINTS.AUTH.REFRESH
  );

  // Update session_id if new one is returned
  if (response.data?.session_id) {
    setSessionId(response.data.session_id);
    console.log('✅ Session refreshed - new session_id stored');
  }
};

export default {
  login,
  logout,
  getUserInfo,
  refreshToken,
  setSessionId,
  getSessionId,
  clearSessionId,
};
